/**
 * The one-shot bank. Every impact, chime, tick and drum in the game is baked here, from
 * scratch, into Float32Arrays at a fixed 44.1 kHz — no files, no OfflineAudioContext, no
 * AudioContext at all. `toAudioBuffers()` later copies them into real AudioBuffers if and
 * when the context ever starts.
 *
 * WHY BAKE RATHER THAN SYNTHESISE LIVE: a collision crunch needs a resonance that sweeps
 * downward over 40 ms and six inharmonic partials with individual decays. Building that
 * live costs ~15 WebAudio nodes per hit; baked, it costs one AudioBufferSourceNode. At the
 * rates gameplay fires cone hits, that is the difference between a stable frame and a
 * garbage-collection spike.
 *
 * Randomness comes exclusively from the pre-drawn RandTable (see dsp.js). No Math.random.
 */

import {
  RandTable, Biquad, ad, ahr, glide, addTone, addNoise, fillNoise, pinkify,
  sweptFilter, softClip, normalize, tailFade, mixInto, seamless,
} from './dsp.js';

/** Musical helper — the chimes are tuned to the music's D-major brightener. */
const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);

/**
 * Every bank entry: name → { seconds, build(out, sr, rand) }.
 * `out` arrives zeroed. Builders add into it and may normalise at the end.
 */
export const RECIPES = {

  // =================================================================== THE THUMP
  /**
   * The signature sound. You hit an open pothole at speed. Four layers:
   *   1. a low sine that falls 78 → 30 Hz in 200 ms — the punch you feel in your chest
   *   2. a 3 ms noise slap, highpassed — the tyre carcass hitting the edge
   *   3. a bandpassed 190 Hz whump — the suspension bottoming out
   *   4. seven inharmonic metal partials with a jittery tremolo — the works-truck body,
   *      the tool rack and everything loose in the back rattling at once
   * It should be satisfying and very slightly painful.
   */
  thump: {
    seconds: 0.85,
    build(out, sr, rand) {
      // 1. sub punch
      addTone(out, sr, {
        freqAt: (t) => glide(t, 78, 30, 0.055),
        ampAt: (t) => 1.05 * ad(t, 0.0035, 0.115),
      });
      // body knock an octave up, dies fast
      addTone(out, sr, {
        freqAt: (t) => glide(t, 168, 62, 0.028),
        ampAt: (t) => 0.5 * ad(t, 0.0015, 0.048),
      });

      // 2. tyre slap — very short, very bright
      const slap = new Float32Array(Math.round(sr * 0.03));
      fillNoise(slap, rand);
      for (let i = 0; i < slap.length; i++) slap[i] *= ad(i / sr, 0.0004, 0.0055);
      new Biquad(sr).highpass(2400, 0.9).run(slap);
      mixInto(out, slap, 0, 0.75);

      // 3. suspension whump
      const whump = new Float32Array(Math.round(sr * 0.3));
      fillNoise(whump, rand);
      for (let i = 0; i < whump.length; i++) whump[i] *= ad(i / sr, 0.002, 0.085);
      new Biquad(sr).bandpass(190, 1.2).run(whump);
      mixInto(out, whump, 0, 2.4);

      // 4. metallic body rattle — inharmonic, each partial with its own decay, all of them
      //    tremolo'd by a slow noisy envelope so it clatters instead of ringing.
      const modes = [431, 613, 827, 1049, 1373, 1811, 2417];
      const taus = [0.30, 0.24, 0.19, 0.16, 0.13, 0.11, 0.08];
      const amps = [0.20, 0.17, 0.15, 0.13, 0.10, 0.08, 0.06];
      // pre-build a shared rattle tremolo: lowpassed noise, so all partials clatter together
      const trem = new Float32Array(out.length);
      fillNoise(trem, rand);
      new Biquad(sr).lowpass(36, 0.7).run(trem);
      let tm = 0;
      for (let i = 0; i < trem.length; i++) { const a = Math.abs(trem[i]); if (a > tm) tm = a; }
      const tinv = tm > 1e-6 ? 1 / tm : 1;
      for (let i = 0; i < trem.length; i++) trem[i] = 0.45 + 0.55 * Math.min(1, Math.abs(trem[i]) * tinv);

      for (let k = 0; k < modes.length; k++) {
        const f = modes[k] * (1 + rand.bi() * 0.012);
        const tau = taus[k], a = amps[k];
        const ph = rand.next() * Math.PI * 2;
        let p = ph;
        const dp = 2 * Math.PI * f / sr;
        // small onset delay per mode so the rattle "spreads"
        const d = Math.round(sr * rand.range(0.004, 0.022));
        for (let i = d; i < out.length; i++) {
          p += dp;
          const t = (i - d) / sr;
          out[i] += Math.sin(p) * a * ad(t, 0.001, tau) * trem[i];
        }
      }

      softClip(out, 0.32);
      normalize(out, 0.99);
      tailFade(out, sr, 14);
    },
  },

  // =================================================================== impacts
  /** Metal collision. Downward resonance sweep + a low thud + four ringing partials. */
  crunch: {
    seconds: 0.5,
    build(out, sr, rand) {
      const n = out.length;
      const noise = new Float32Array(n);
      fillNoise(noise, rand);
      for (let i = 0; i < n; i++) noise[i] *= ad(i / sr, 0.0008, 0.055);
      // the sweep is the whole character: 2100 Hz down to 380 Hz in ~60 ms
      sweptFilter(noise, sr, 'bandpass', (t) => glide(t, 2100, 380, 0.035), 1.6);
      mixInto(out, noise, 0, 3.0);

      addTone(out, sr, { freqAt: (t) => glide(t, 130, 52, 0.035), ampAt: (t) => 0.85 * ad(t, 0.002, 0.085) });

      const modes = [786, 1131, 1592, 2214];
      for (let k = 0; k < modes.length; k++) {
        addTone(out, sr, {
          freq: modes[k] * (1 + rand.bi() * 0.02),
          ampAt: (t) => (0.16 - k * 0.03) * ad(t, 0.0015, 0.14 - k * 0.025),
          phase: rand.next() * 6.283,
        });
      }
      softClip(out, 0.25);
      normalize(out, 0.96);
      tailFade(out, sr, 12);
    },
  },

  /**
   * THE CONE BONK. Cones are the running joke and hitting one must be funny, so this is
   * deliberately cartoon: a hollow pitched "boink" that falls a fourth in 50 ms, two
   * hollow body modes that beat against each other, a plastic scuff, and a vibrato tail
   * that wobbles like an upturned bucket.
   */
  coneBonk: {
    seconds: 0.45,
    build(out, sr, rand) {
      // the boink — sine plus a third partial gives the hollow plastic edge
      addTone(out, sr, { freqAt: (t) => glide(t, 430, 236, 0.022), ampAt: (t) => 0.9 * ad(t, 0.0012, 0.055) });
      addTone(out, sr, { freqAt: (t) => 3.02 * glide(t, 430, 236, 0.022), ampAt: (t) => 0.22 * ad(t, 0.0012, 0.026) });

      // two body modes, slightly out of tune → audible beating = "hollow"
      addTone(out, sr, { freq: 505, ampAt: (t) => 0.28 * ad(t, 0.003, 0.13), phase: rand.next() * 6.283 });
      addTone(out, sr, { freq: 683, ampAt: (t) => 0.22 * ad(t, 0.003, 0.115), phase: rand.next() * 6.283 });

      // plastic scuff
      const scuff = new Float32Array(Math.round(sr * 0.05));
      fillNoise(scuff, rand);
      for (let i = 0; i < scuff.length; i++) scuff[i] *= ad(i / sr, 0.0006, 0.016);
      new Biquad(sr).bandpass(3200, 2.0).run(scuff);
      mixInto(out, scuff, 0, 2.2);

      // the comedy tail: 190 Hz with a 9 Hz vibrato, i.e. "bwoing"
      addTone(out, sr, {
        freqAt: (t) => 190 + 14 * Math.sin(6.283 * 9 * t),
        ampAt: (t) => 0.30 * ad(t, 0.01, 0.16),
      });
      normalize(out, 0.95);
      tailFade(out, sr, 12);
    },
  },

  /** A cone being set down. Light, quick, unglamorous. */
  conePlace: {
    seconds: 0.28,
    build(out, sr, rand) {
      const tick = new Float32Array(Math.round(sr * 0.04));
      fillNoise(tick, rand);
      for (let i = 0; i < tick.length; i++) tick[i] *= ad(i / sr, 0.0005, 0.009);
      new Biquad(sr).bandpass(2400, 1.3).run(tick);
      mixInto(out, tick, 0, 2.4);
      addTone(out, sr, { freqAt: (t) => glide(t, 640, 500, 0.02), ampAt: (t) => 0.45 * ad(t, 0.001, 0.05) });
      addTone(out, sr, { freq: 218, ampAt: (t) => 0.22 * ad(t, 0.002, 0.06) });
      normalize(out, 0.8);
      tailFade(out, sr, 10);
    },
  },

  /** Suspension landing after air. Sub thud + spring + tyre slap. */
  landing: {
    seconds: 0.6,
    build(out, sr, rand) {
      addTone(out, sr, { freqAt: (t) => glide(t, 92, 42, 0.045), ampAt: (t) => 1.0 * ad(t, 0.003, 0.09) });
      const slap = new Float32Array(Math.round(sr * 0.12));
      fillNoise(slap, rand);
      for (let i = 0; i < slap.length; i++) slap[i] *= ad(i / sr, 0.001, 0.022);
      new Biquad(sr).bandpass(1100, 1.1).run(slap);
      mixInto(out, slap, 0, 2.0);
      // leaf-spring creak
      addTone(out, sr, { freqAt: (t) => glide(t, 340, 250, 0.09), ampAt: (t) => 0.18 * ad(t, 0.02, 0.14) });
      addTone(out, sr, { freq: 617, ampAt: (t) => 0.10 * ad(t, 0.012, 0.1), phase: rand.next() * 6.283 });
      normalize(out, 0.95);
      tailFade(out, sr, 12);
    },
  },

  /** Air-brake / gear pneumatic chuff. */
  gearShift: {
    seconds: 0.35,
    build(out, sr, rand) {
      const chuff = new Float32Array(Math.round(sr * 0.22));
      fillNoise(chuff, rand);
      for (let i = 0; i < chuff.length; i++) chuff[i] *= ad(i / sr, 0.004, 0.045);
      sweptFilter(chuff, sr, 'bandpass', (t) => glide(t, 2600, 900, 0.05), 1.0);
      mixInto(out, chuff, 0, 2.6);
      addTone(out, sr, { freq: 128, ampAt: (t) => 0.35 * ad(t, 0.002, 0.038) });
      normalize(out, 0.75);
      tailFade(out, sr, 10);
    },
  },

  /** Puddle / slush splash. */
  splash: {
    seconds: 0.55,
    build(out, sr, rand) {
      const n = out.length;
      const w = new Float32Array(n);
      fillNoise(w, rand);
      for (let i = 0; i < n; i++) w[i] *= ad(i / sr, 0.004, 0.07);
      sweptFilter(w, sr, 'bandpass', (t) => 700 + 2600 * Math.min(1, t / 0.09), 0.8);
      mixInto(out, w, 0, 2.4);
      // droplet tail
      const tailN = n;
      const drops = new Float32Array(tailN);
      fillNoise(drops, rand);
      for (let i = 0; i < tailN; i++) drops[i] *= 0.25 * ad(i / sr, 0.03, 0.14);
      new Biquad(sr).highpass(3400, 0.8).run(drops);
      mixInto(out, drops, 0, 1.6);
      normalize(out, 0.85);
      tailFade(out, sr, 12);
    },
  },

  /** Something big passing close. Near-miss whoosh. */
  whoosh: {
    seconds: 0.55,
    build(out, sr, rand) {
      const n = out.length;
      const w = new Float32Array(n);
      fillNoise(w, rand);
      const T = n / sr;
      for (let i = 0; i < n; i++) {
        const t = i / sr;
        // symmetric swell
        const x = t / T;
        w[i] *= Math.sin(Math.PI * Math.min(1, Math.max(0, x))) ** 1.6;
      }
      sweptFilter(w, sr, 'bandpass', (t) => 320 + 1500 * Math.sin(Math.PI * Math.min(1, t / T)), 1.4);
      mixInto(out, w, 0, 3.0);
      normalize(out, 0.7);
      tailFade(out, sr, 20);
    },
  },

  /** Ville de Montréal air horn. Two notes a fifth apart, deliberately obnoxious. */
  horn: {
    seconds: 1.0,
    build(out, sr) {
      const env = (t) => ahr(t, 0.02, 0.62, 0.10);
      // sawtooth pair
      addTone(out, sr, { shape: 'sawtooth', freq: 138.6, ampAt: (t) => 0.42 * env(t) });
      addTone(out, sr, { shape: 'sawtooth', freq: 207.7, ampAt: (t) => 0.36 * env(t) });
      addTone(out, sr, { shape: 'sawtooth', freq: 139.9, ampAt: (t) => 0.24 * env(t) });
      // horn resonance
      new Biquad(sr).bandpass(760, 0.9).run(out);
      const body = new Float32Array(out.length);
      body.set(out);
      new Biquad(sr).peaking(1500, 1.6, 8).run(body);
      out.set(body);
      softClip(out, 0.4);
      normalize(out, 0.9);
      tailFade(out, sr, 20);
    },
  },

  // =================================================================== repair
  /** Valve opening — the spray about to start. */
  sprayStart: {
    seconds: 0.35,
    build(out, sr, rand) {
      const n = out.length;
      const w = new Float32Array(n);
      fillNoise(w, rand);
      for (let i = 0; i < n; i++) w[i] *= ad(i / sr, 0.008, 0.075);
      sweptFilter(w, sr, 'bandpass', (t) => glide(t, 3400, 1100, 0.05), 1.1);
      mixInto(out, w, 0, 2.8);
      addTone(out, sr, { freqAt: (t) => glide(t, 90, 150, 0.06), ampAt: (t) => 0.25 * ad(t, 0.006, 0.07) });
      normalize(out, 0.8);
      tailFade(out, sr, 10);
    },
  },

  /** Valve closing. */
  sprayEnd: {
    seconds: 0.3,
    build(out, sr, rand) {
      const n = out.length;
      const w = new Float32Array(n);
      fillNoise(w, rand);
      for (let i = 0; i < n; i++) w[i] *= ad(i / sr, 0.002, 0.05);
      sweptFilter(w, sr, 'bandpass', (t) => glide(t, 1800, 420, 0.04), 1.3);
      mixInto(out, w, 0, 2.4);
      normalize(out, 0.65);
      tailFade(out, sr, 10);
    },
  },

  /**
   * Milestone tick at 25 / 50 / 75 / 90 %. A small bright bell. Played back at increasing
   * `rate` per milestone so the four ticks rise — the meter climbing, audible.
   */
  tick: {
    seconds: 0.18,
    build(out, sr, rand) {
      addTone(out, sr, { freq: 1046.5, ampAt: (t) => 0.55 * ad(t, 0.0012, 0.042) });
      addTone(out, sr, { freq: 2093.0, ampAt: (t) => 0.22 * ad(t, 0.0012, 0.028) });
      addTone(out, sr, { freq: 3140.0, ampAt: (t) => 0.09 * ad(t, 0.0012, 0.018) });
      const click = new Float32Array(Math.round(sr * 0.01));
      fillNoise(click, rand);
      for (let i = 0; i < click.length; i++) click[i] *= ad(i / sr, 0.0002, 0.0022);
      new Biquad(sr).highpass(4000, 0.8).run(click);
      mixInto(out, click, 0, 1.2);
      normalize(out, 0.85);
      tailFade(out, sr, 8);
    },
  },

  /** The trowel-slap transient that fires on the frame the hole reads 100 %. */
  sealThunk: {
    seconds: 0.32,
    build(out, sr, rand) {
      addTone(out, sr, { freqAt: (t) => glide(t, 165, 78, 0.03), ampAt: (t) => 0.8 * ad(t, 0.002, 0.055) });
      const slap = new Float32Array(Math.round(sr * 0.08));
      fillNoise(slap, rand);
      for (let i = 0; i < slap.length; i++) slap[i] *= ad(i / sr, 0.001, 0.015);
      new Biquad(sr).bandpass(880, 0.9).run(slap);
      mixInto(out, slap, 0, 2.2);
      normalize(out, 0.85);
      tailFade(out, sr, 10);
    },
  },

  /**
   * Completion chime. A D-major arpeggio (D5 F#5 A5 D6), which is the brightening chord of
   * the music's own progression, so the payoff lands IN KEY with whatever is playing.
   * Bell timbre = inharmonic partial ratios 1 / 2.01 / 3.03 / 4.21 with staggered decays.
   */
  chime: {
    seconds: 2.3,
    build(out, sr) {
      const notes = [74, 78, 81, 86];               // D5 F#5 A5 D6
      const offs = [0.0, 0.055, 0.110, 0.170];
      const ratios = [1, 2.01, 3.03, 4.21];
      const partAmp = [1.0, 0.42, 0.20, 0.10];
      const partTau = [0.85, 0.55, 0.34, 0.22];
      for (let n = 0; n < notes.length; n++) {
        const f0 = mtof(notes[n]);
        const t0 = offs[n];
        for (let p = 0; p < ratios.length; p++) {
          addTone(out, sr, {
            freq: f0 * ratios[p],
            ampAt: (t) => (t < t0 ? 0 : 0.30 * partAmp[p] * ad(t - t0, 0.004, partTau[p] * (1 - n * 0.12))),
          });
        }
      }
      // the bloom: a low D2 under it so the completion has weight
      addTone(out, sr, { freq: mtof(38), ampAt: (t) => 0.55 * ad(t, 0.006, 0.30) });
      addTone(out, sr, { freq: mtof(50), ampAt: (t) => 0.25 * ad(t, 0.006, 0.22) });
      normalize(out, 0.92);
      tailFade(out, sr, 40);
    },
  },

  /**
   * PERFECT. Six notes instead of four, a wider voicing with the add9, a sub octave drop,
   * and a shimmer swell so the payoff keeps arriving after the transient is gone.
   */
  chimePerfect: {
    seconds: 3.1,
    build(out, sr, rand) {
      const notes = [74, 78, 81, 85, 86, 90];       // D5 F#5 A5 C#6 D6 F#6
      const offs = [0.0, 0.045, 0.090, 0.135, 0.180, 0.235];
      const ratios = [1, 2.01, 3.02, 4.19, 5.4];
      const partAmp = [1.0, 0.46, 0.24, 0.13, 0.07];
      const partTau = [1.35, 0.85, 0.55, 0.34, 0.22];
      for (let n = 0; n < notes.length; n++) {
        const f0 = mtof(notes[n]);
        const t0 = offs[n];
        for (let p = 0; p < ratios.length; p++) {
          addTone(out, sr, {
            freq: f0 * ratios[p],
            ampAt: (t) => (t < t0 ? 0 : 0.26 * partAmp[p] * ad(t - t0, 0.004, partTau[p] * (1 - n * 0.08))),
          });
        }
      }
      // sub drop
      addTone(out, sr, { freqAt: (t) => glide(t, 147, 73.4, 0.12), ampAt: (t) => 0.6 * ad(t, 0.006, 0.42) });
      // shimmer swell — arrives late, which is what makes it feel bigger than `chime`
      const n = out.length;
      const sh = new Float32Array(n);
      fillNoise(sh, rand);
      for (let i = 0; i < n; i++) {
        const t = i / sr;
        sh[i] *= 0.30 * Math.min(1, t / 0.5) * Math.exp(-Math.max(0, t - 0.5) / 0.85);
      }
      new Biquad(sr).highpass(5200, 0.7).run(sh);
      new Biquad(sr).peaking(9000, 1.2, 6).run(sh);
      mixInto(out, sh, 0, 1.4);
      normalize(out, 0.97);
      tailFade(out, sr, 60);
    },
  },

  /** Time extension. Crazy Taxi's "+5 sec" energy: two rising bells and glitter. */
  timeBell: {
    seconds: 1.2,
    build(out, sr, rand) {
      const notes = [81, 88];                        // A5, E6
      const offs = [0.0, 0.09];
      for (let i = 0; i < notes.length; i++) {
        const f = mtof(notes[i]), t0 = offs[i];
        addTone(out, sr, { freq: f, ampAt: (t) => (t < t0 ? 0 : 0.5 * ad(t - t0, 0.003, 0.42)) });
        addTone(out, sr, { freq: f * 2.01, ampAt: (t) => (t < t0 ? 0 : 0.18 * ad(t - t0, 0.003, 0.26)) });
        addTone(out, sr, { freq: f * 3.04, ampAt: (t) => (t < t0 ? 0 : 0.08 * ad(t - t0, 0.003, 0.16)) });
      }
      const g = new Float32Array(out.length);
      fillNoise(g, rand);
      for (let i = 0; i < g.length; i++) g[i] *= 0.14 * ad(i / sr, 0.02, 0.22);
      new Biquad(sr).highpass(6000, 0.8).run(g);
      mixInto(out, g, 0, 1.5);
      normalize(out, 0.9);
      tailFade(out, sr, 30);
    },
  },

  /** Patch cured — soft, warm, "that one is done". */
  cureShimmer: {
    seconds: 1.0,
    build(out, sr, rand) {
      const bell = (t, t0, tau) => (t < t0 ? 0 : ad(t - t0, 0.05, tau));
      addTone(out, sr, { freq: 880, ampAt: (t) => 0.24 * bell(t, 0, 0.28) });
      addTone(out, sr, { freq: 1320, ampAt: (t) => 0.15 * bell(t, 0.05, 0.22) });
      const n = out.length;
      const air = new Float32Array(n);
      fillNoise(air, rand);
      for (let i = 0; i < n; i++) {
        const t = i / sr;
        air[i] *= 0.22 * Math.min(1, t / 0.14) * Math.exp(-Math.max(0, t - 0.14) / 0.28);
      }
      new Biquad(sr).bandpass(3000, 0.7).run(air);
      mixInto(out, air, 0, 1.6);
      normalize(out, 0.65);
      tailFade(out, sr, 30);
    },
  },

  /** Patch ruined. A descending, gritty groan. Annoying on purpose. */
  ruin: {
    seconds: 0.8,
    build(out, sr, rand) {
      addTone(out, sr, { shape: 'sawtooth', freqAt: (t) => glide(t, 420, 88, 0.14), ampAt: (t) => 0.5 * ad(t, 0.006, 0.20) });
      addTone(out, sr, { shape: 'square', freqAt: (t) => glide(t, 210, 45, 0.14), ampAt: (t) => 0.22 * ad(t, 0.006, 0.18) });
      const grit = new Float32Array(out.length);
      fillNoise(grit, rand);
      for (let i = 0; i < grit.length; i++) grit[i] *= 0.4 * ad(i / sr, 0.004, 0.12);
      sweptFilter(grit, sr, 'bandpass', (t) => glide(t, 1600, 380, 0.1), 1.2);
      mixInto(out, grit, 0, 2.0);
      new Biquad(sr).lowpass(2600, 0.8).run(out);
      softClip(out, 0.5);
      normalize(out, 0.8);
      tailFade(out, sr, 16);
    },
  },

  /** Slalom chain blip. Short, rising, arcade. Rate-shifted per link by the caller. */
  chainBlip: {
    seconds: 0.2,
    build(out, sr) {
      addTone(out, sr, { shape: 'square', freqAt: (t) => glide(t, 620, 980, 0.02), ampAt: (t) => 0.35 * ad(t, 0.001, 0.05) });
      addTone(out, sr, { freqAt: (t) => 2 * glide(t, 620, 980, 0.02), ampAt: (t) => 0.12 * ad(t, 0.001, 0.03) });
      new Biquad(sr).lowpass(6000, 0.7).run(out);
      normalize(out, 0.8);
      tailFade(out, sr, 8);
    },
  },

  /** Slalom broken. A dull, deflating thud. */
  breakThud: {
    seconds: 0.4,
    build(out, sr) {
      addTone(out, sr, { shape: 'square', freqAt: (t) => glide(t, 150, 78, 0.06), ampAt: (t) => 0.5 * ad(t, 0.004, 0.11) });
      new Biquad(sr).lowpass(900, 0.9).run(out);
      normalize(out, 0.7);
      tailFade(out, sr, 12);
    },
  },

  /** Timer warning. Two clipped square pulses. Urgent, not pretty. */
  warnBeep: {
    seconds: 0.26,
    build(out, sr) {
      const pulse = (t, t0) => (t >= t0 && t < t0 + 0.055 ? ahr(t - t0, 0.002, 0.04, 0.008) : 0);
      addTone(out, sr, { shape: 'square', freq: 1180, ampAt: (t) => 0.4 * (pulse(t, 0) + pulse(t, 0.09)) });
      addTone(out, sr, { shape: 'square', freq: 1186, ampAt: (t) => 0.25 * (pulse(t, 0) + pulse(t, 0.09)) });
      new Biquad(sr).bandpass(1400, 1.4).run(out);
      normalize(out, 0.75);
      tailFade(out, sr, 8);
    },
  },

  // =================================================================== drums
  kick: {
    seconds: 0.36,
    build(out, sr, rand) {
      // A punk kick, not a club kick: it lands at 56 Hz rather than sinking to 46, and its
      // tail is short. Measured, the deeper version put 57 % of the whole music mix below
      // 60 Hz and buried the guitars.
      addTone(out, sr, { freqAt: (t) => glide(t, 132, 56, 0.022), ampAt: (t) => 1.0 * ad(t, 0.002, 0.072) });
      const click = new Float32Array(Math.round(sr * 0.012));
      fillNoise(click, rand);
      for (let i = 0; i < click.length; i++) click[i] *= ad(i / sr, 0.0003, 0.0028);
      new Biquad(sr).bandpass(2200, 1.0).run(click);
      mixInto(out, click, 0, 1.7);
      softClip(out, 0.28);
      normalize(out, 0.97);
      tailFade(out, sr, 10);
    },
  },

  snare: {
    seconds: 0.3,
    build(out, sr, rand) {
      const n = out.length;
      const w = new Float32Array(n);
      fillNoise(w, rand);
      for (let i = 0; i < n; i++) w[i] *= ad(i / sr, 0.0009, 0.062);
      new Biquad(sr).highpass(420, 0.8).run(w);
      new Biquad(sr).peaking(3400, 1.1, 5).run(w);
      mixInto(out, w, 0, 1.5);
      addTone(out, sr, { freq: 192, ampAt: (t) => 0.34 * ad(t, 0.0012, 0.048) });
      addTone(out, sr, { freq: 331, ampAt: (t) => 0.24 * ad(t, 0.0012, 0.038) });
      softClip(out, 0.3);
      normalize(out, 0.95);
      tailFade(out, sr, 10);
    },
  },

  hatC: {
    seconds: 0.09,
    build(out, sr, rand) {
      fillNoise(out, rand);
      for (let i = 0; i < out.length; i++) out[i] *= ad(i / sr, 0.0004, 0.013);
      new Biquad(sr).highpass(6500, 0.8).run(out);
      new Biquad(sr).peaking(9500, 1.5, 5).run(out);
      normalize(out, 0.55);
      tailFade(out, sr, 5);
    },
  },

  hatO: {
    seconds: 0.34,
    build(out, sr, rand) {
      fillNoise(out, rand);
      for (let i = 0; i < out.length; i++) out[i] *= ad(i / sr, 0.0006, 0.085);
      new Biquad(sr).highpass(6000, 0.8).run(out);
      new Biquad(sr).peaking(11000, 1.4, 4).run(out);
      normalize(out, 0.5);
      tailFade(out, sr, 12);
    },
  },

  crash: {
    seconds: 1.6,
    build(out, sr, rand) {
      fillNoise(out, rand);
      for (let i = 0; i < out.length; i++) out[i] *= ad(i / sr, 0.002, 0.42);
      new Biquad(sr).highpass(2600, 0.7).run(out);
      new Biquad(sr).peaking(6500, 0.9, 6).run(out);
      // a couple of low bell modes stop it being pure hiss
      addTone(out, sr, { freq: 430, ampAt: (t) => 0.06 * ad(t, 0.004, 0.35), phase: rand.next() * 6.283 });
      addTone(out, sr, { freq: 712, ampAt: (t) => 0.05 * ad(t, 0.004, 0.30), phase: rand.next() * 6.283 });
      normalize(out, 0.62);
      tailFade(out, sr, 40);
    },
  },

  rim: {
    seconds: 0.14,
    build(out, sr, rand) {
      const click = new Float32Array(out.length);
      fillNoise(click, rand);
      for (let i = 0; i < click.length; i++) click[i] *= ad(i / sr, 0.0003, 0.006);
      new Biquad(sr).bandpass(1750, 2.4).run(click);
      mixInto(out, click, 0, 2.4);
      addTone(out, sr, { freq: 410, ampAt: (t) => 0.2 * ad(t, 0.0008, 0.022) });
      normalize(out, 0.7);
      tailFade(out, sr, 6);
    },
  },

  tom: {
    seconds: 0.36,
    build(out, sr, rand) {
      addTone(out, sr, { freqAt: (t) => glide(t, 205, 122, 0.06), ampAt: (t) => 0.8 * ad(t, 0.0018, 0.10) });
      const w = new Float32Array(Math.round(sr * 0.04));
      fillNoise(w, rand);
      for (let i = 0; i < w.length; i++) w[i] *= ad(i / sr, 0.0006, 0.01);
      new Biquad(sr).bandpass(1400, 1.2).run(w);
      mixInto(out, w, 0, 0.9);
      normalize(out, 0.85);
      tailFade(out, sr, 10);
    },
  },
};

/** Loop-length noise beds, in seconds. Long enough that the loop point is not a rhythm. */
export const LOOP_SECONDS = 2.75;

/**
 * Bake every recipe plus the two looping noise beds.
 * @param {RandTable} rand   deterministic uniforms, pre-drawn in AudioSystem.init()
 * @param {number} sr        render rate (AUDIO_TUNING.renderRate)
 * @returns {{ oneShots: Map<string, Float32Array>, white: Float32Array, pink: Float32Array, sr:number, ms:number }}
 */
export function bakeBank(rand, sr) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  const oneShots = new Map();

  // Deterministic per-recipe start offsets into the random table, so adding a new recipe
  // cannot change the noise inside an existing one.
  const names = Object.keys(RECIPES).sort();
  for (let i = 0; i < names.length; i++) {
    const name = names[i];
    const r = RECIPES[name];
    rand.seekTo(i * 4099);                      // 4099 is prime; keeps the windows disjoint
    const n = Math.round(r.seconds * sr);
    const buf = new Float32Array(n);
    r.build(buf, sr, rand);
    oneShots.set(name, buf);
  }

  // Looping beds. Rendered a little long, then cross-faded to be seamless.
  rand.seekTo(60013);
  const extra = Math.round(sr * 0.05);
  const whiteRaw = new Float32Array(Math.round(LOOP_SECONDS * sr) + extra);
  fillNoise(whiteRaw, rand);
  const white = seamless(whiteRaw, extra);

  rand.seekTo(61027);
  const pinkRaw = new Float32Array(Math.round(LOOP_SECONDS * sr) + extra);
  fillNoise(pinkRaw, rand);
  pinkify(pinkRaw);
  normalize(pinkRaw, 0.9);
  const pink = seamless(pinkRaw, extra);

  const ms = (typeof performance !== 'undefined' ? performance.now() : 0) - t0;
  return { oneShots, white, pink, sr, ms };
}

/**
 * Copy the baked Float32Arrays into real AudioBuffers. Called once, the first time the
 * AudioContext actually starts. `sr` is baked into the AudioBuffer so the browser resamples
 * for us and the content is identical on a 44.1 k and a 48 k device.
 */
export function toAudioBuffers(actx, baked) {
  const out = new Map();
  for (const [name, data] of baked.oneShots) {
    const b = actx.createBuffer(1, data.length, baked.sr);
    b.getChannelData(0).set(data);
    out.set(name, b);
  }
  const mk = (data) => { const b = actx.createBuffer(1, data.length, baked.sr); b.getChannelData(0).set(data); return b; };
  return { oneShots: out, white: mk(baked.white), pink: mk(baked.pink) };
}

export { RandTable };
